import { type PropsWithChildren } from 'react'
import { type SystemCssProperties } from '@dhl-ui/theme'
import { elevation, type Elevation } from '../../theme/elevation'
import { Box, type BoxProps } from '../Box'
import { type BaseProps } from '../../types'

export type PaperProps = PropsWithChildren<{
  /**
   * @default false
   */
  isRounded?: boolean

  /**
   * @default false
   */
  isOutlined?: boolean

  /**
   * By default we make sure the inner content will not overflow. (also to make sure hover effects are in sync with radius etc etc)
   * But in some cases you want to be in control yourself.
   *
   * @default false
   */
  hasNoOverflowHidden?: boolean

  /**
   * Option to get a 'dark' background instead of its default 'light'
   * @default 'light
   */
  background?: 'light' | 'dark'

  /**
   * elevation of the paper
   * @default undefined
   */
  elevation?: Elevation
}> &
  Pick<BoxProps, 'onDragOver' | 'onDragLeave' | 'onDrop'> &
  BaseProps<
    HTMLDivElement,
    never,
    Pick<SystemCssProperties, 'aspectRatio' | 'display' | 'height' | 'textAlign' | 'width' | 'cursor'>
  >

export const Paper = ({
  isRounded = false,
  isOutlined = false,
  background = 'light',
  hasNoOverflowHidden = false,
  children,
  sx,
  elevation: elevationKey,
  ...rest
}: PaperProps) => (
  <Box
    className="DhlPaper"
    sx={{
      ...sx,
      border: ({ palette }) => (isOutlined ? `1px solid ${palette.grey[300]}` : undefined),
      borderRadius: +isRounded,
      overflow: hasNoOverflowHidden ? undefined : 'hidden',
      backgroundColor: background === 'light' ? 'background.paper' : 'background.default',
      ...(elevationKey ? { boxShadow: elevation[elevationKey] } : {}),
    }}
    {...rest}
  >
    {children}
  </Box>
)
